import React, {
  useState, useEffect, useMemo, useContext,
} from 'react';
import PropTypes from 'prop-types';
import './GeneratorProductTable.less';
import * as api from 'util/api.js';
import {
  Table as AntTable, Tooltip, Avatar, Button, Switch,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CustomTable, GeneratorModal } from 'components';
import { TableConfigContext } from 'util/TableConfig';

const { Column } = AntTable;

const SEARCHABLE_COLUMNS = ['name', 'oneLiner'];

const GeneratorProductTable = ({
  type,
  selectionType,
  pluralLabel,
  GeneratorFormContent,
  initialFormValues,
  onGenerate,
  generatorStatus,
  generatorOutput,
  onGeneratorReset,
  flattenFormData,
  modalWidth,
}) => {
  const { getTableConfig, setTableConfig } = useContext(TableConfigContext);
  const [products, setProducts] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [generatorModalOpen, setGeneratorModalOpen] = useState(false);
  /** Modal open: selected products merged with SKUs (labels / Mailchimp builders). */
  const [productsForModal, setProductsForModal] = useState(null);
  /** Tap badges: omit rows without a tap badge image from the table when true */
  const [hideWithoutTapBadge, setHideWithoutTapBadge] = useState(true);

  const tableId = `generator-product-table-${type}-${pluralLabel}`;

  useEffect(() => {
    api.getProducts().then(setProducts);
  }, []);

  const visibleProducts = useMemo(() => {
    if (!products) return null;
    if (type !== 'tap-badge' || !hideWithoutTapBadge) return products;
    return products.filter((p) => !!p.tapBadgeUrl);
  }, [products, hideWithoutTapBadge, type]);

  /**
   * Stable reference: CustomTable writes dataSource into TableConfig in an effect.
   * A new array each render caused an infinite update loop.
   */
  const tableDataSource = useMemo(() => {
    if (!visibleProducts) return null;
    return visibleProducts.map((product) => ({
      ...product,
      key: product.id,
      disabled: !(product.tapBadgeUrl) && type !== 'mailchimp' && type !== 'mailchimp-new',
    }));
  }, [visibleProducts, type]);

  useEffect(() => {
    if (type !== 'tap-badge' || !visibleProducts) return;
    const cfg = getTableConfig(tableId);
    const visibleKeys = new Set(visibleProducts.map((p) => p.id));
    const next = (cfg.selected || []).filter((r) => visibleKeys.has(r.key));
    if (next.length !== (cfg.selected || []).length) {
      setTableConfig(tableId, { ...cfg, selected: next });
    }
  }, [visibleProducts, type, tableId, getTableConfig, setTableConfig]);

  useEffect(() => {
    let cancelled = false;
    if (!generatorModalOpen) {
      setProductsForModal(null);
    } else {
      (async () => {
        if (!selectedProducts.length) {
          if (!cancelled) setProductsForModal([]);
          return;
        }
        try {
          const enriched = await Promise.all(
            selectedProducts.map(async (p) => {
              try {
                const skus = await api.getProductSkus(p.id);
                return { ...p, skus };
              } catch {
                return { ...p, skus: [] };
              }
            }),
          );
          if (!cancelled) setProductsForModal(enriched);
        } catch {
          if (!cancelled) {
            setProductsForModal(selectedProducts.map((p) => ({ ...p, skus: [] })));
          }
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [generatorModalOpen, selectedProducts]);

  // useEffect(() => {
  //   // Close the modal after a successful generation
  //   if (!generating && !errorGenerating && generatorModalOpen) {
  //     setGeneratorModalOpen(false);
  //   }
  // }, [generating]);

  const onGenerateClicked = () => {
    setGeneratorModalOpen(true);
  };

  const handleClose = () => {
    setGeneratorModalOpen(false);

    // Avoid flickering back to form view
    setTimeout(() => onGeneratorReset(), 300);
  };

  return (
    <>
      <CustomTable
        id={tableId}
        pluralLabel="products"
        searchableColumns={SEARCHABLE_COLUMNS}
        enableSelection
        selectionType={selectionType}
        onSelectionChange={setSelectedProducts}
        dataSource={tableDataSource}
        disabledMessage="A tap badge hasn't been added for this product"
        controls={(
          <span className="generator-product-table-controls">
            <Tooltip title={!selectedProducts.length ? `Select products to generate ${pluralLabel} for first` : null}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                // Disable when no products are selected or loading
                disabled={!selectedProducts.length || !products}
                onClick={onGenerateClicked}
              >
                {`Generate ${pluralLabel}`}
              </Button>
            </Tooltip>
            {type === 'tap-badge' && (
              <Switch
                checked={hideWithoutTapBadge}
                onChange={setHideWithoutTapBadge}
                checkedChildren="Empty"
                unCheckedChildren="Empty"
              />
            )}
          </span>
      )}
      >
        <Column
          title={null}
          dataIndex="tapBadgeUrl"
          key="tapBadge"
          width={64}
          render={(tapBadgeUrl) => (
            <Avatar src={tapBadgeUrl} />
          )}
        />
        <Column
          title="Name"
          dataIndex="name"
        />
        <Column
          title="ABV"
          key="abv"
          render={({ abv, abvMin, abvMax }) => {
            const abvIsRange = abvMin !== null || abvMax !== null;

            if (abv === null && !abvIsRange) {
              return <div className="empty-cell">N/A</div>;
            }

            return (abv !== null ? (
              `${abv}%`
            ) : (
              `${abvMin === null ? '?' : `${abvMin}%`} - ${abvMax === null ? '?' : `${abvMax}%`}`
            ));
          }}
        />
        <Column title="One Liner" dataIndex="oneLiner" key="oneLiner" />
      </CustomTable>
      <GeneratorModal
        type={type}
        selectionType={selectionType}
        pluralLabel={pluralLabel}
        products={generatorModalOpen ? (productsForModal ?? selectedProducts) : selectedProducts}
        open={generatorModalOpen}
        onClose={handleClose}
        onGenerate={onGenerate}
        generatorStatus={generatorStatus}
        generatorOutput={generatorOutput}
        FormContent={GeneratorFormContent}
        initialFormValues={initialFormValues}
        flattenFormData={flattenFormData}
        width={modalWidth}
      />
    </>
  );
};

GeneratorProductTable.propTypes = {
  type: PropTypes.oneOf(['mailchimp', 'mailchimp-new', 'tap-badge', 'keg', 'slab', 'case', 'pallet']).isRequired,
  selectionType: PropTypes.oneOf(['single', 'multiple']).isRequired,
  pluralLabel: PropTypes.string.isRequired,
  GeneratorFormContent: PropTypes.elementType.isRequired,
  initialFormValues: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onGenerate: PropTypes.func.isRequired,
  generatorStatus: PropTypes.shape({
    active: PropTypes.bool,
    error: PropTypes.string,
    success: PropTypes.bool,
  }),
  generatorOutput: PropTypes.shape({
    type: PropTypes.oneOf(['pdf']),
    // eslint-disable-next-line react/forbid-prop-types
    data: PropTypes.any,
  }),
  onGeneratorReset: PropTypes.func,
  flattenFormData: PropTypes.bool,
  modalWidth: PropTypes.number,
};

GeneratorProductTable.defaultProps = {
  initialFormValues: {},
  flattenFormData: true,
  modalWidth: 400,
  generatorStatus: {
    active: false,
    error: null,
    success: false,
  },
  generatorOutput: null,
  onGeneratorReset: () => {},
};

export default GeneratorProductTable;
